/**
 * Organizational Characteristics Utility
 *
 * Extracts and processes organizational characteristics from Business Scoping.
 * These characteristics add regulatory overlays, domain weight boosts, and
 * narrative framing on top of the base industry threat profile.
 *
 * Characteristics do NOT influence maturity scoring - only TIDA outputs.
 */

/**
 * Modifier definitions for each organizational characteristic.
 * This is the single source of truth for all characteristic-based adjustments.
 */
const CHARACTERISTIC_MODIFIERS = {
  'nonprofit': {
    label: 'Nonprofit / Tax-Exempt',
    regulatoryOverlays: ['Form 990 public disclosure', 'Donor data protection'],
    accreditationContext: 'May be subject to accreditation body requirements (CARF, Joint Commission, etc.) that impose additional security expectations beyond regulatory minimums.',
    costModelAdjustment: 'Resource-constrained funding model. Recommendations should emphasize grant-eligible and budget-efficient approaches.',
    narrativeFrame: 'As a nonprofit organization, security investments compete directly with mission delivery for limited funding. Recommendations are calibrated to maximize risk reduction within resource constraints while meeting donor and accreditation expectations.'
  },

  'publicly-traded': {
    label: 'Publicly Traded / SEC-Registered',
    regulatoryOverlays: [
      'SEC Regulation S-K Item 106 (cybersecurity risk disclosure)',
      'SEC 8-K Item 1.05 (material incident disclosure within 4 business days)',
      'Sarbanes-Oxley Act (internal controls over financial reporting)',
      'Proxy statement cybersecurity governance disclosure'
    ],
    breachNotificationAdditions: [
      {
        entity: 'SEC (Securities and Exchange Commission)',
        condition: 'Material cybersecurity incidents must be disclosed via Form 8-K within 4 business days of determining materiality.',
        threshold: 'Incident determined to be material to investors',
        required: false,
        method: 'SEC EDGAR filing',
        source: 'organizational-characteristic'
      }
    ],
    domainWeightBoosts: {
      'governance-strategy': 0.10,
      'program-risk-reporting': 0.10,
      'data-management': 0.05
    },
    threatScenarioAdditions: [
      'Material nonpublic information (MNPI) exfiltration for insider trading',
      'Financial statement manipulation or data integrity attacks'
    ],
    narrativeFrame: 'As a publicly traded entity, cybersecurity governance is subject to SEC disclosure requirements and board-level fiduciary obligations. Material incidents trigger mandatory public disclosure within 4 business days, creating additional reputational and market impact beyond operational disruption.'
  },

  'government-contractor': {
    label: 'Government Contractor / CUI Handler',
    regulatoryOverlays: [
      'DFARS 252.204-7012 (Safeguarding Covered Defense Information)',
      'NIST SP 800-171 (Protecting CUI in Nonfederal Systems)',
      'CMMC 2.0 (Cybersecurity Maturity Model Certification)'
    ],
    breachNotificationAdditions: [
      {
        entity: 'DoD Cyber Crime Center (DC3)',
        condition: 'Cyber incidents affecting covered contractor information systems must be reported within 72 hours.',
        threshold: 'Incident affects covered contractor information systems or CUI',
        required: false,
        method: 'DIBNet portal (https://dibnet.dod.mil)',
        source: 'organizational-characteristic'
      }
    ],
    domainWeightBoosts: {
      'access-identity': 0.05,
      'data-management': 0.10,
      'operations-technology': 0.05
    },
    narrativeFrame: 'As a government contractor handling CUI, your organization must meet NIST 800-171 controls and is subject to CMMC certification requirements. Non-compliance risks contract loss and debarment in addition to standard breach consequences.'
  },

  'payment-card-processing': {
    label: 'Payment Card Data Processing',
    regulatoryOverlays: [
      'PCI DSS v4.0',
      'Card brand compliance programs (Visa, Mastercard, etc.)'
    ],
    breachNotificationAdditions: [
      {
        entity: 'Payment Card Brands / Acquiring Bank',
        condition: 'Card brand notification required for any confirmed or suspected compromise of cardholder data. Timeline varies by brand (typically immediate).',
        threshold: 'Confirmed or suspected compromise of cardholder data',
        required: false,
        method: 'Through acquiring bank or payment processor',
        source: 'organizational-characteristic'
      }
    ],
    domainWeightBoosts: {
      'access-identity': 0.05,
      'technology-environment': 0.05,
      'operations-technology': 0.05
    },
    narrativeFrame: 'Payment card data processing subjects the organization to PCI DSS requirements with potential for significant non-compliance fines and loss of processing privileges.'
  },

  'multi-state-operations': {
    label: 'Multi-State Operations',
    regulatoryOverlays: [
      'Multiple state breach notification laws apply',
      'Most restrictive state timeline governs'
    ],
    breachNotificationAdjustments: {
      note: 'Multi-state operations require compliance with breach notification laws in every state where affected individuals reside. The most restrictive applicable timeline must be used. Several states (California, Colorado, Connecticut, Virginia, etc.) have comprehensive privacy laws with additional requirements beyond breach notification.'
    },
    narrativeFrame: 'Multi-state operations increase regulatory complexity. Breach notification must comply with the most restrictive applicable state law, and comprehensive state privacy laws may impose additional data handling requirements.'
  }
}

/**
 * Framework-specific regulatory overlays.
 * These apply when a framework is selected in Business Scoping (rf-1),
 * regardless of the threat profile selected.
 */
const FRAMEWORK_OVERLAYS = {
  'hipaa': {
    label: 'HIPAA',
    frameworks: ['HIPAA', 'HITECH'],
    breachNotificationAdditions: [
      {
        entity: 'HHS Office for Civil Rights',
        condition: 'Breaches affecting 500+ individuals must be reported within 60 days.',
        threshold: '500+ individuals affected',
        required: false,
        method: 'HHS OCR Breach Portal',
        timeline: '60 days from discovery',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'HHS Office for Civil Rights',
        citation: '45 CFR § 164.404-410 (HIPAA Breach Notification Rule)',
        enforcementBody: 'U.S. Department of Health and Human Services',
        url: 'https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-C/part-164/subpart-D',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'HIPAA compliance requires safeguards for protected health information (PHI), with breach notification to HHS OCR and affected individuals.',
    breachNotificationDays: 60
  },
  'pci-dss': {
    label: 'PCI DSS',
    frameworks: ['PCI DSS v4.0'],
    breachNotificationAdditions: [
      {
        entity: 'Payment Card Brands / Acquiring Bank',
        condition: 'Card brand notification required for any confirmed or suspected compromise of cardholder data.',
        threshold: 'Confirmed or suspected compromise of cardholder data',
        required: false,
        method: 'Through acquiring bank or payment processor',
        timeline: 'Immediate',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'PCI Security Standards Council',
        citation: 'PCI DSS v4.0 Requirements 12.10.2-12.10.6 (Incident Response)',
        enforcementBody: 'Payment Card Brands (Visa, Mastercard, Amex, Discover)',
        url: 'https://docs-prv.pcisecuritystandards.org/PCI%20DSS/Standard/PCI-DSS-v4_0.pdf',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'PCI DSS compliance subjects the organization to payment card security requirements with potential for significant non-compliance fines.'
  },
  'glba': {
    label: 'GLBA',
    frameworks: ['GLBA'],
    breachNotificationAdditions: [
      {
        entity: 'Primary Financial Regulator (OCC / FDIC / Federal Reserve / NCUA)',
        condition: 'Notification required for unauthorized access to customer financial information per GLBA § 501(b).',
        threshold: 'Unauthorized access to customer financial information',
        required: false,
        method: 'Per primary regulator guidance',
        timeline: 'As soon as possible',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'Primary Federal Financial Regulator',
        citation: 'GLBA Title V, Subtitle A (15 U.S.C. §§ 6801-6809)',
        enforcementBody: 'OCC / FDIC / Federal Reserve / NCUA (depending on charter type)',
        url: 'https://www.law.cornell.edu/uscode/text/15/chapter-94B',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'GLBA requires financial institutions to implement safeguards for customer financial information. Enforcement is by the institution\'s primary federal regulator.'
  },
  'ftc-safeguards': {
    label: 'FTC Safeguards Rule',
    frameworks: ['FTC Safeguards Rule'],
    breachNotificationAdditions: [
      {
        entity: 'Federal Trade Commission',
        condition: 'Non-bank financial institutions must notify the FTC of security events affecting 500+ consumers within 60 days.',
        threshold: '500+ consumers affected',
        required: false,
        method: 'FTC notification',
        timeline: '60 days from discovery',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'Federal Trade Commission',
        citation: '16 CFR Part 314 (FTC Safeguards Rule)',
        enforcementBody: 'Federal Trade Commission',
        url: 'https://www.ecfr.gov/current/title-16/chapter-I/subchapter-C/part-314',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'The FTC Safeguards Rule (16 CFR Part 314) applies to non-bank financial institutions not regulated by another federal agency. It mandates a comprehensive information security program including risk assessments, access controls, encryption, MFA, and incident response.'
  },
  'cmmc': {
    label: 'CMMC',
    frameworks: ['CMMC 2.0', 'NIST SP 800-171'],
    breachNotificationAdditions: [
      {
        entity: 'DoD Cyber Crime Center (DC3)',
        condition: 'Cyber incidents affecting covered contractor information systems must be reported within 72 hours.',
        threshold: 'Incident affects covered contractor information systems or CUI',
        required: false,
        method: 'DIBNet portal',
        timeline: '72 hours',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'Department of Defense',
        citation: 'DFARS 252.204-7012 / NIST SP 800-171',
        enforcementBody: 'DoD Office of the Under Secretary of Defense for Acquisition & Sustainment',
        url: 'https://www.acquisition.gov/dfars/252.204-7012-safeguarding-covered-defense-information-and-cyber-incident-reporting',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'CMMC certification requires implementation of NIST 800-171 controls for handling Controlled Unclassified Information (CUI).'
  },
  'soc2': {
    label: 'SOC 2',
    frameworks: ['SOC 2 Type II'],
    authorityAdditions: [
      {
        name: 'AICPA',
        citation: 'AICPA Trust Services Criteria (TSC)',
        enforcementBody: 'Third-Party CPA Auditors / Customer Contracts',
        url: 'https://www.aicpa-cima.com/resources/landing/system-and-organization-controls-soc-suite-of-services',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'SOC 2 compliance demonstrates security controls to customers through annual audits of trust service criteria.'
  },
  'ccpa': {
    label: 'CCPA/CPRA',
    frameworks: ['CCPA', 'CPRA'],
    breachNotificationAdditions: [
      {
        entity: 'California Attorney General',
        condition: 'Breaches affecting 500+ California residents require AG notification.',
        threshold: '500+ California residents affected',
        required: false,
        method: 'CA AG notification portal',
        timeline: '72 hours',
        source: 'framework-selection'
      }
    ],
    authorityAdditions: [
      {
        name: 'California Privacy Protection Agency',
        citation: 'Cal. Civ. Code § 1798.100-199.100 (CCPA/CPRA)',
        enforcementBody: 'California Attorney General / California Privacy Protection Agency',
        url: 'https://leginfo.legislature.ca.gov/faces/codes_displayText.xhtml?division=3.&part=4.&lawCode=CIV&title=1.81.5',
        source: 'framework-selection'
      }
    ],
    narrativeFrame: 'California privacy laws require specific consumer rights and breach notification to the California AG.'
  }
}

/**
 * Get framework overlays based on selected frameworks from Business Scoping (rf-1).
 *
 * @param {Object} businessScopingResponses - Business scoping responses object
 * @returns {Object} Framework overlay modifiers
 */
export function getFrameworkOverlays(businessScopingResponses) {
  const response = businessScopingResponses?.['rf-1']
  if (!response) {
    return { hasOverlays: false, selected: [], modifiers: getEmptyModifiers() }
  }

  // Handle various response formats
  let selectedValues = []
  if (response.value) {
    selectedValues = Array.isArray(response.value) ? response.value : [response.value]
  } else if (Array.isArray(response)) {
    selectedValues = response
  } else if (typeof response === 'string') {
    selectedValues = [response]
  }

  if (selectedValues.length === 0) {
    return { hasOverlays: false, selected: [], modifiers: getEmptyModifiers() }
  }

  // Build merged overlay set
  const merged = getEmptyModifiers()
  const appliedFrameworks = []

  selectedValues.forEach(value => {
    const overlay = FRAMEWORK_OVERLAYS[value]
    if (!overlay) return

    appliedFrameworks.push(overlay.label)

    if (overlay.frameworks) {
      merged.regulatoryOverlays.push(...overlay.frameworks)
    }

    if (overlay.breachNotificationAdditions) {
      merged.breachNotificationAdditions.push(...overlay.breachNotificationAdditions)
    }

    if (overlay.authorityAdditions) {
      merged.authorityAdditions.push(...overlay.authorityAdditions)
    }

    if (overlay.narrativeFrame) {
      merged.narrativeFrames.push(overlay.narrativeFrame)
    }
  })

  return {
    hasOverlays: appliedFrameworks.length > 0,
    selected: selectedValues,
    selectedLabels: appliedFrameworks,
    modifiers: merged
  }
}

/**
 * Get the modifiers for a specific characteristic value.
 *
 * @param {string} value - The characteristic value (e.g., 'nonprofit', 'publicly-traded')
 * @returns {Object|null} The modifiers for the characteristic, or null if not found
 */
function getModifiersForValue(value) {
  return CHARACTERISTIC_MODIFIERS[value] || null
}

/**
 * Get the display label for a characteristic value.
 *
 * @param {string} value - The characteristic value
 * @returns {string} The display label
 */
function getLabelForCharacteristic(value) {
  return CHARACTERISTIC_MODIFIERS[value]?.label || value
}

/**
 * Create empty modifiers object for when no characteristics are selected.
 */
function getEmptyModifiers() {
  return {
    regulatoryOverlays: [],
    breachNotificationAdditions: [],
    breachNotificationAdjustments: null,
    domainWeightBoosts: {},
    threatScenarioAdditions: [],
    narrativeFrames: [],
    costModelNotes: [],
    accreditationContext: null,
    authorityAdditions: []
  }
}

/**
 * Merge modifiers from all selected characteristics into a single object.
 * Regulatory overlays and breach notification additions are concatenated.
 * Domain weight boosts are summed (but capped at 1.0 per domain when applied).
 * Narrative frames are collected for combined narrative generation.
 *
 * @param {Array<string>} selectedValues - Array of selected characteristic values
 * @returns {Object} Merged modifiers object
 */
function mergeCharacteristicModifiers(selectedValues) {
  const merged = getEmptyModifiers()

  selectedValues.forEach(value => {
    const modifiers = getModifiersForValue(value)
    if (!modifiers) return

    if (modifiers.regulatoryOverlays) {
      merged.regulatoryOverlays.push(...modifiers.regulatoryOverlays)
    }

    if (modifiers.breachNotificationAdditions) {
      merged.breachNotificationAdditions.push(...modifiers.breachNotificationAdditions)
    }

    if (modifiers.breachNotificationAdjustments) {
      merged.breachNotificationAdjustments = modifiers.breachNotificationAdjustments
    }

    if (modifiers.domainWeightBoosts) {
      Object.entries(modifiers.domainWeightBoosts).forEach(([domain, boost]) => {
        merged.domainWeightBoosts[domain] = (merged.domainWeightBoosts[domain] || 0) + boost
      })
    }

    if (modifiers.threatScenarioAdditions) {
      merged.threatScenarioAdditions.push(...modifiers.threatScenarioAdditions)
    }

    if (modifiers.narrativeFrame) {
      merged.narrativeFrames.push(modifiers.narrativeFrame)
    }

    if (modifiers.costModelAdjustment) {
      merged.costModelNotes.push(modifiers.costModelAdjustment)
    }

    if (modifiers.accreditationContext) {
      merged.accreditationContext = modifiers.accreditationContext
    }
  })

  return merged
}

/**
 * Extract and process organizational characteristics from Business Scoping responses.
 *
 * @param {Object} businessScopingResponses - The business scoping responses object
 * @returns {Object} Structured characteristics object for threat context and narrative
 */
export function getOrgCharacteristics(businessScopingResponses) {
  const response = businessScopingResponses?.['oc-1']
  if (!response) {
    return { hasCharacteristics: false, selected: [], modifiers: getEmptyModifiers() }
  }

  // oc-1 is multi-select, so response.value should be an array
  // Handle both array format and potential single value format
  let selectedValues = []

  if (response.value) {
    selectedValues = Array.isArray(response.value) ? response.value : [response.value]
  } else if (Array.isArray(response)) {
    // Handle case where response is the array directly
    selectedValues = response
  } else if (typeof response === 'string') {
    // Handle case where response is a single string value
    selectedValues = [response]
  }

  // Filter out "none-of-the-above"
  const activeSelections = selectedValues.filter(v => v !== 'none-of-the-above')

  if (activeSelections.length === 0) {
    return { hasCharacteristics: false, selected: [], modifiers: getEmptyModifiers() }
  }

  // Build merged modifier set from all selected characteristics
  const merged = mergeCharacteristicModifiers(activeSelections)

  return {
    hasCharacteristics: true,
    selected: activeSelections,
    selectedLabels: activeSelections.map(v => getLabelForCharacteristic(v)),
    modifiers: merged
  }
}

/**
 * Build the full regulatory context by merging base profile context with org characteristic overlays
 * and framework-specific overlays from Business Scoping.
 *
 * @param {Object} profileRegulatoryContext - Base regulatory context from threat profile
 * @param {Object} orgCharacteristics - Result from getOrgCharacteristics()
 * @param {Object} frameworkOverlays - Result from getFrameworkOverlays() (optional)
 * @returns {Object} Enhanced regulatory context with all overlays applied
 */
export function buildFullRegulatoryContext(profileRegulatoryContext, orgCharacteristics, frameworkOverlays = null) {
  const base = JSON.parse(JSON.stringify(profileRegulatoryContext || {}))

  // Helper to apply modifiers to base
  const applyModifiers = (mods, source) => {
    if (!mods) return

    // Append regulatory overlays (deduplicate)
    if (mods.regulatoryOverlays?.length > 0) {
      const existingFrameworks = base.applicableFrameworks || base.frameworks || []
      const newFrameworks = mods.regulatoryOverlays.filter(f => !existingFrameworks.includes(f))
      if (newFrameworks.length > 0) {
        base.applicableFrameworks = [...existingFrameworks, ...newFrameworks]
      }
    }

    // Append breach notification entities
    if (mods.breachNotificationAdditions?.length > 0) {
      if (!base.breachNotification) {
        base.breachNotification = {}
      }
      if (!base.breachNotification.notificationOrder) {
        base.breachNotification.notificationOrder = []
      }

      const existingEntities = base.breachNotification.notificationOrder.map(n => n.entity?.toLowerCase() || '')
      const maxOrder = base.breachNotification.notificationOrder.length > 0
        ? Math.max(...base.breachNotification.notificationOrder.map(n => n.rank || n.order || 0))
        : 0

      // Keywords for fuzzy dedup of notification entities
      const ENTITY_DEDUP_KEYWORDS = ['occ', 'fdic', 'ncua', 'federal reserve', 'federal regulator', 'federal financial', 'state banking', 'fincen', 'pci', 'security standards council']

      mods.breachNotificationAdditions.forEach((addition, index) => {
        const addLower = (addition.entity || '').toLowerCase()
        // Avoid duplicates — exact match or keyword overlap
        const isDuplicate = existingEntities.some(existing => {
          if (existing === addLower) return true
          if (existing.includes(addLower) || addLower.includes(existing)) return true
          // Check keyword overlap
          return ENTITY_DEDUP_KEYWORDS.some(kw => existing.includes(kw) && addLower.includes(kw))
        })
        if (!isDuplicate) {
          base.breachNotification.notificationOrder.push({
            rank: maxOrder + index + 1,
            ...addition
          })
        }
      })
    }

    // Track overlay sources for UI
    if (!base.overlaysSources) {
      base.overlaysSources = []
    }
    if (source) {
      base.overlaysSources.push(source)
    }
  }

  // Apply org characteristic overlays
  if (orgCharacteristics?.hasCharacteristics) {
    applyModifiers(orgCharacteristics.modifiers, 'org-characteristics')

    // Add accreditation context if applicable
    if (orgCharacteristics.modifiers?.accreditationContext) {
      base.accreditationContext = orgCharacteristics.modifiers.accreditationContext
    }

    // Add multi-state adjustment note
    if (orgCharacteristics.modifiers?.breachNotificationAdjustments) {
      if (!base.breachNotification) {
        base.breachNotification = {}
      }
      base.breachNotification.multiStateNote = orgCharacteristics.modifiers.breachNotificationAdjustments.note
    }

    // Tag org characteristic overlays
    base.characteristicOverlays = {
      regulatoryFrameworks: orgCharacteristics.modifiers.regulatoryOverlays || [],
      breachNotificationAdditions: (orgCharacteristics.modifiers.breachNotificationAdditions || []).map(a => a.entity),
      hasMultiStateComplexity: !!orgCharacteristics.modifiers.breachNotificationAdjustments
    }
  }

  // Apply framework overlays from Business Scoping (rf-1)
  if (frameworkOverlays?.hasOverlays) {
    applyModifiers(frameworkOverlays.modifiers, 'framework-selection')

    // Add authority additions to breach notification
    if (frameworkOverlays.modifiers.authorityAdditions?.length > 0) {
      if (!base.breachNotification) {
        base.breachNotification = {}
      }
      if (!base.breachNotification.additionalAuthorities) {
        base.breachNotification.additionalAuthorities = []
      }

      // Add authorities, avoiding duplicates by name or keyword overlap
      const existingAuthNames = base.breachNotification.additionalAuthorities.map(a => (a.name || '').toLowerCase())
      const baseEntities = (base.breachNotification.notificationOrder || []).map(n => (n.entity || '').toLowerCase())
      const allExisting = [...existingAuthNames, ...baseEntities]
      const AUTH_DEDUP_KW = ['occ', 'fdic', 'ncua', 'federal reserve', 'federal regulator', 'federal financial', 'state banking', 'fincen', 'pci', 'security standards council']

      frameworkOverlays.modifiers.authorityAdditions.forEach(auth => {
        const authLower = (auth.name || '').toLowerCase()
        const isDup = allExisting.some(existing => {
          if (existing === authLower || existing.includes(authLower) || authLower.includes(existing)) return true
          return AUTH_DEDUP_KW.some(kw => existing.includes(kw) && authLower.includes(kw))
        })
        if (!isDup) {
          base.breachNotification.additionalAuthorities.push(auth)
        }
      })
    }

    // Tag framework overlays
    base.frameworkOverlays = {
      selectedFrameworks: frameworkOverlays.selectedLabels || [],
      regulatoryFrameworks: frameworkOverlays.modifiers.regulatoryOverlays || [],
      breachNotificationAdditions: (frameworkOverlays.modifiers.breachNotificationAdditions || []).map(a => a.entity)
    }
  }

  return base
}

/**
 * Apply domain weight boosts from org characteristics to base profile weights.
 * Boosts are additive and capped at 1.0 per domain.
 *
 * @param {Object} baseDomainWeights - Domain weights from threat profile
 * @param {Object} orgCharacteristics - Result from getOrgCharacteristics()
 * @returns {Object} Adjusted domain weights
 */
export function applyDomainWeightBoosts(baseDomainWeights, orgCharacteristics) {
  if (!orgCharacteristics?.hasCharacteristics) return { ...baseDomainWeights }

  const adjusted = { ...baseDomainWeights }
  const boosts = orgCharacteristics.modifiers.domainWeightBoosts || {}

  Object.entries(boosts).forEach(([domain, boost]) => {
    if (adjusted[domain] !== undefined) {
      adjusted[domain] = Math.min(1.0, adjusted[domain] + boost)
    }
  })

  return adjusted
}

/**
 * Build the combined threat narrative from all context layers.
 *
 * @param {string} profileDescription - Base industry narrative from threat profile
 * @param {Object} segmentContext - From deriveMarketSegment()
 * @param {Object} orgCharacteristics - From getOrgCharacteristics()
 * @param {Object} engagementContext - From getEngagementDriverContext() (optional)
 * @returns {Object} Structured narrative sections
 */
export function buildCombinedNarrative(
  profileDescription,
  segmentContext,
  orgCharacteristics,
  engagementContext = null
) {
  const sections = []

  // Industry threat landscape (always present)
  if (profileDescription) {
    sections.push(profileDescription)
  }

  // Market segment context
  if (segmentContext?.modifiers?.narrativeModifier) {
    sections.push(segmentContext.modifiers.narrativeModifier)
  }

  // Org characteristic narratives
  if (orgCharacteristics?.hasCharacteristics && orgCharacteristics.modifiers.narrativeFrames.length > 0) {
    sections.push(...orgCharacteristics.modifiers.narrativeFrames)
  }

  // Engagement driver framing (if present)
  if (engagementContext?.hasDriver && engagementContext.reportFraming?.emphasis) {
    sections.push(`This assessment emphasizes ${engagementContext.reportFraming.emphasis}.`)
  }

  return {
    fullNarrative: sections.join('\n\n'),
    industrySection: profileDescription || null,
    segmentSection: segmentContext?.modifiers?.narrativeModifier || null,
    characteristicSections: orgCharacteristics?.modifiers?.narrativeFrames || [],
    engagementSection: engagementContext?.reportFraming?.context || null,
    threatActorContext: segmentContext?.modifiers?.threatActorDescription || null,
    regulatoryContext: segmentContext?.modifiers?.regulatoryNote || null,
    costModelNotes: orgCharacteristics?.modifiers?.costModelNotes || []
  }
}

/**
 * Get characteristic selection summary for display.
 *
 * @param {Object} orgCharacteristics - Result from getOrgCharacteristics()
 * @returns {Object} Summary for UI display
 */
export function getCharacteristicsSummary(orgCharacteristics) {
  if (!orgCharacteristics?.hasCharacteristics) {
    return {
      hasAny: false,
      count: 0,
      labels: [],
      badges: []
    }
  }

  return {
    hasAny: true,
    count: orgCharacteristics.selected.length,
    labels: orgCharacteristics.selectedLabels,
    badges: orgCharacteristics.selected.map(v => ({
      value: v,
      label: getLabelForCharacteristic(v),
      hasRegulatory: (CHARACTERISTIC_MODIFIERS[v]?.regulatoryOverlays?.length || 0) > 0,
      hasBreachNotification: (CHARACTERISTIC_MODIFIERS[v]?.breachNotificationAdditions?.length || 0) > 0
    }))
  }
}

export default {
  getOrgCharacteristics,
  getFrameworkOverlays,
  buildFullRegulatoryContext,
  applyDomainWeightBoosts,
  buildCombinedNarrative,
  getCharacteristicsSummary,
  CHARACTERISTIC_MODIFIERS,
  FRAMEWORK_OVERLAYS
}
